import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Users, Shield, AlertTriangle, Scale, ChevronLeft, Target, Activity, Cpu, Layers, BookOpen, Briefcase, Zap, HelpCircle, HardDrive, CheckCircle } from 'lucide-react';
import { useBrand } from '@/hooks/useBrand';
import { Breadcrumb } from '@/components/ui/Breadcrumb';

export function TermsPage() {
  const navigate = useNavigate();
  const brand = useBrand();

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Terms of Service' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 selection:bg-primary-500/30 overflow-x-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      {/* Background Ambience */}
      <div className="fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-[1000px] h-[1000px] bg-primary-600/5 blur-[150px] rounded-full translate-x-1/2 -translate-y-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-amber-600/5 blur-[120px] rounded-full -translate-x-1/2 translate-y-1/2 opacity-30" />
        <div className="absolute inset-0 bg-[url('/carbon-fibre.png')] opacity-20" />
      </div>

      <main id="main-content" role="main" className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 py-12">
        <div className="flex items-center justify-between mb-12">
          <Breadcrumb items={breadcrumbItems} onBack={() => navigate(-1)} />
          <button
            onClick={() => navigate(-1)}
            className="p-3 bg-white/5 border border-white/5 rounded-2xl hover:bg-white/10 transition-all"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Page Header */}
        <header className="mb-16 border-b border-gray-200 pb-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-10">
            <div>
              <div className="flex items-center gap-6 mb-4">
                <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center shadow-sm">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                  Terms of Service
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-600" />
                  Last Updated: December 15, 2024
                </span>
                <div className="w-px h-4 bg-gray-300" />
                <span className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-4 h-4" />
                  Legally Binding Agreement
                </span>
              </div>
            </div>
            <div className="px-6 py-4 bg-white border border-gray-200 rounded-xl">
              <span className="text-xs font-medium text-gray-500 block mb-1">Contract Type</span>
              <span className="text-lg font-bold text-amber-600">Legal Agreement</span>
            </div>
          </div>
        </header>

        {/* Content Volumes */}
        <div className="grid gap-16">
          {/* Agreement */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-amber-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Target className="w-6 h-6 text-amber-600" />
                Agreement to Terms
              </h2>
            </div>
            <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
              <p className="text-lg text-gray-700 leading-relaxed">
                Welcome to {brand.name}. These Terms of Service ("Terms") govern your use of our service platform.
                By accessing {brand.name}, you agree to be legally bound by these terms. Failure to comply may result in account termination.
              </p>
            </div>
          </section>

          {/* Platform Services */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Layers className="w-6 h-6 text-primary-600" />
                Platform Services
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <Users className="w-5 h-5 text-primary-600" />
                    Customer Features
                  </h3>
                </div>
                <ul className="space-y-3">
                  {[
                    'Job Posting (Create Service Requests)',
                    'Quote Comparison (Review Proposals)',
                    'Secure Messaging (Communication)',
                    'Review System (Provider Feedback)'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-primary-300 transition-all">
                      <div className="w-2 h-2 bg-primary-600 rounded-full" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-primary-600" />
                    Provider Features
                  </h3>
                </div>
                <ul className="space-y-3">
                  {[
                    'Profile Verification (Business Credentials)',
                    'Job Alerts (Opportunity Notifications)',
                    'Quote Submission (Pricing Proposals)',
                    'Payment Processing (Secure Transactions)'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-primary-300 transition-all">
                      <div className="w-2 h-2 bg-primary-600 rounded-full" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* User Responsibilities */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-amber-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Shield className="w-6 h-6 text-amber-600" />
                User Responsibilities
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-gray-900 uppercase mb-4 flex items-center gap-3">
                  <Target className="w-4 h-4 text-emerald-600" />
                  Required Compliance
                </h4>
                <div className="grid gap-3">
                  {[
                    'Maintain Account Security (Password Protection)',
                    'Report Security Breaches (Notification Required)',
                    'Provide Accurate Information (Current Data)',
                    'Use Platform Lawfully (Legal Compliance)'
                  ].map((it, idx) => (
                    <div key={idx} className="p-4 bg-white border border-gray-200 rounded-lg flex items-center justify-between hover:border-emerald-300 transition-all">
                      <span className="text-sm text-gray-700">{it}</span>
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-bold text-amber-600 uppercase mb-4 flex items-center gap-3">
                  <AlertTriangle className="w-4 h-4" />
                  Prohibited Activities
                </h4>
                <div className="p-6 bg-rose-50 border border-rose-200 rounded-xl">
                  <ul className="space-y-2">
                    {[
                      'Providing False Information',
                      'Harassment or Abuse',
                      'Fee Circumvention',
                      'Unauthorized Access Attempts'
                    ].map((it, idx) => (
                      <li key={idx} className="text-sm text-rose-700 flex items-center gap-3">
                        <div className="w-1.5 h-1.5 bg-rose-600 rounded-full" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Fees and Payments */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Scale className="w-6 h-6 text-blue-600" />
                Fees and Payments
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <Activity className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Service Fee</h4>
                <span className="text-3xl font-bold text-blue-600 mb-2">5%</span>
                <p className="text-xs text-gray-600">Platform commission on completed jobs</p>
              </div>

              <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <Cpu className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Payment Processing</h4>
                <span className="text-3xl font-bold text-blue-600 mb-2">3%</span>
                <p className="text-xs text-gray-600">Transaction processing fee</p>
              </div>

              <div className="p-8 bg-primary-50 border-2 border-primary-200 rounded-xl shadow-sm flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Premium Provider</h4>
                <span className="text-3xl font-bold text-gray-900 mb-2">€29.99</span>
                <p className="text-sm font-medium text-primary-600">Monthly Subscription</p>
              </div>
            </div>
          </section>



          {/* Contact Information */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <HelpCircle className="w-6 h-6 text-blue-600" />
                Questions About Terms
              </h2>
            </div>

            <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
                <div className="flex-1">
                  <p className="text-sm text-gray-700 mb-6 leading-relaxed">
                    If you have questions about these Terms of Service, please contact us through the following channels.
                  </p>
                  <div className="flex flex-wrap gap-8 text-center md:text-left">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Email</span>
                      <span className="text-sm font-medium text-gray-900">{brand.email}</span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Phone</span>
                      <span className="text-sm font-medium text-gray-900">{brand.config.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg min-w-[240px]">
                  <div className="flex flex-col items-center gap-3">
                    <HardDrive className="w-10 h-10 text-blue-600" />
                    <span className="text-xs text-gray-600 text-center leading-relaxed">
                      Terms Version 1.0<br />
                      Effective December 15, 2024
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="pt-8 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Last updated: December 15, 2024 • Version 1.0
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}